import { DEFAULT_ORG_SIZE, EMAIL_MAX_LENGTH, INDUSTRIES, NAME_MAX_LENGTH, ORG_SIZES, type IndustryKey, type OrgSizeKey } from "@sagot/constants";
import { z } from "zod";
import {
  DEFAULT_COUNTRY,
  REGISTER_DRAFT_KEY,
  REGISTER_PLAN_NAMES,
  REGISTER_STEPS,
  SIGNUP_PLAN_KEY,
  type RegisterPlanKey,
  type RegisterStep,
} from "./register-constants";
import { isEmail, isNameOk, isOrgOk, isPasswordOk } from "./register-rules";

/** Everything the visitor typed into the wizard. Only the fields in `persistedSchema` outlive a page load. */
export interface RegisterDraft {
  email: string;
  name: string;
  password: string;
  orgName: string;
  industry: IndustryKey | null;
  size: OrgSizeKey;
  country: string;
  terms: boolean;
  dpa: boolean;
  marketing: boolean;
}

export const EMPTY_DRAFT: RegisterDraft = {
  email: "",
  name: "",
  password: "",
  orgName: "",
  industry: null,
  size: DEFAULT_ORG_SIZE,
  country: DEFAULT_COUNTRY,
  terms: false,
  dpa: false,
  marketing: false,
};

/** What is kept in sessionStorage: no password, no consent ticks, no security-check token. */
const persistedSchema = z.object({
  email: z.string().max(EMAIL_MAX_LENGTH),
  name: z.string().max(NAME_MAX_LENGTH),
  orgName: z.string().max(NAME_MAX_LENGTH),
  industry: z.enum(INDUSTRIES).nullable(),
  size: z.enum(ORG_SIZES),
  country: z.string().length(2),
  marketing: z.boolean(),
});

export function readDraft(): RegisterDraft {
  try {
    const raw = sessionStorage.getItem(REGISTER_DRAFT_KEY);
    if (!raw) return EMPTY_DRAFT;
    const parsed = persistedSchema.safeParse(JSON.parse(raw));
    return parsed.success ? { ...EMPTY_DRAFT, ...parsed.data } : EMPTY_DRAFT;
  } catch {
    return EMPTY_DRAFT;
  }
}

export function writeDraft(draft: RegisterDraft): void {
  try {
    const { email, name, orgName, industry, size, country, marketing } = draft;
    sessionStorage.setItem(REGISTER_DRAFT_KEY, JSON.stringify({ email, name, orgName, industry, size, country, marketing }));
  } catch {
    // Storage blocked: the wizard still works within this page view.
  }
}

export function clearDraft(): void {
  try {
    sessionStorage.removeItem(REGISTER_DRAFT_KEY);
  } catch {
    // Nothing stored.
  }
}

/** `?plan=` value from the pricing page, or null when it is not one of the plans. */
export function parsePlan(value: string | null | undefined): RegisterPlanKey | null {
  const key = (value ?? "").trim().toLowerCase();
  return key in REGISTER_PLAN_NAMES ? (key as RegisterPlanKey) : null;
}

/** The plan is kept in this tab so it survives the code step and reaches onboarding. It is never sent anywhere else. */
export function readSignupPlan(): RegisterPlanKey | null {
  try {
    return parsePlan(sessionStorage.getItem(SIGNUP_PLAN_KEY));
  } catch {
    return null;
  }
}

export function writeSignupPlan(plan: RegisterPlanKey): void {
  try {
    sessionStorage.setItem(SIGNUP_PLAN_KEY, plan);
  } catch {
    // Storage blocked: the plan still travels in the URL.
  }
}

export function clearSignupPlan(): void {
  try {
    sessionStorage.removeItem(SIGNUP_PLAN_KEY);
  } catch {
    // Nothing stored.
  }
}

export function parseStep(value: string | null): RegisterStep {
  return REGISTER_STEPS.find((step) => step === value) ?? "email";
}

export const stepNumber = (step: RegisterStep): number => REGISTER_STEPS.indexOf(step) + 1;

interface Progress {
  /** Email the API accepted a sign-up for (the code pane waits for it). */
  pendingEmail: string | null;
  /** The code was accepted: the ready pane may show. */
  verified: boolean;
}

/** The furthest pane the visitor may be on, given what is filled in (a deep link or a refresh lands here, never past it). */
export function furthestStep(draft: RegisterDraft, progress: Progress): RegisterStep {
  if (progress.verified) return "ready";
  if (!isEmail(draft.email)) return "email";
  if (progress.pendingEmail !== null && progress.pendingEmail === draft.email.trim()) return "verify";
  if (!isNameOk(draft.name) || !isPasswordOk(draft.password)) return "about";
  if (!isOrgOk(draft.orgName, draft.industry)) return "organization";
  return "consents";
}

/** Clamp: the requested pane, or the furthest allowed one when the request is further. */
export function resolveStep(requested: RegisterStep, furthest: RegisterStep): RegisterStep {
  return stepNumber(requested) > stepNumber(furthest) ? furthest : requested;
}
